import { deleteProviderAccount, getProviderAuthorizeUrl } from '../api/participant/providerAccounts';
import { makeUrl } from '../api/makeUrl';

export interface ProviderAccountDeleteData {
    messageID: string;
    accountID: string;
}

export function getRedirectTarget(): string {
    return window.location.protocol + '//' + window.location.host + makeUrl('cfhrprovideraccounts/redirecttarget');
}

export async function linkAccount(
    providerID: string,
    redirectPath: string | null | undefined,
    accountID: string | null | undefined,
    openNewWindow: boolean | undefined,
    windowWidth?: number,
    windowHeight?: number,
): Promise<void> {
    if (!redirectPath) {
        redirectPath = window.location.pathname + window.location.hash;
    }

    const rootUrl: string | undefined = (window as any).CareEv?.BaseUrl;
    if (rootUrl && rootUrl.length > 0 && redirectPath.substring(0, rootUrl.length).toUpperCase() === rootUrl.toUpperCase()) {
        redirectPath = redirectPath.substring(rootUrl.length);
    }

    // DEV: Safari blocks popups in AJAX responses. Work-around is to open a blank window before the request and set the URL later
    let providerAuthWindow: Window | null = null;
    if (openNewWindow) {
        let windowFeatures: string | undefined;
        if (windowWidth && windowHeight) {
            const w = windowWidth;
            const h = windowHeight;
            const y = window.top!.outerHeight / 2 + window.top!.screenY - h / 2;
            const x = window.top!.outerWidth / 2 + window.top!.screenX - w / 2;
            windowFeatures = `width=${w},height=${h},top=${y},left=${x}`;
        }
        providerAuthWindow = window.open(makeUrl('home/loading'), 'providerauth', windowFeatures);
    }

    return getProviderAuthorizeUrl({
        ProviderID: providerID,
        LoginState: {
            FinalRedirectPath: redirectPath,
            ExistingAccountID: accountID ?? null,
            SuppressErrorPage: !!openNewWindow,
        },
        RedirectUriFallback: getRedirectTarget(),
    }).then(
        (response) => {
            if (providerAuthWindow) {
                providerAuthWindow.location.href = response.AuthorizeUrlWithQuery;
            } else {
                window.location.href = response.AuthorizeUrlWithQuery;
            }
        },
        (error) => {
            if (providerAuthWindow) {
                providerAuthWindow.close();
            }
            const reason: string = error?.message ?? 'Get provider authorize url failed';
            console.error(reason);
            console.error(error);
            return Promise.reject(reason);
        },
    );
}

export function getNotifyParentWindowRedirectTarget(messageID: string | null | undefined | false): string | null {
    if (messageID) {
        return (
            'cfhrprovideraccounts/complete?messageID=' +
            messageID +
            '&success=%SUCCESSVALUE%&errorMessage=%ERRORMESSAGE%'
        );
    }
    return null;
}

export async function connectExternalAccount(
    externalAccountProviderID: string,
    messageID: string | null | undefined,
    openNewWindow: boolean | undefined,
    windowWidth?: number,
    windowHeight?: number,
): Promise<void> {
    return new Promise((resolve, reject) => {
        // DEV: setTimeout is a work-around for Firefox blocking new windows in message handlers
        window.setTimeout(() => {
            linkAccount(
                externalAccountProviderID,
                getNotifyParentWindowRedirectTarget(openNewWindow ? messageID : null),
                null,
                openNewWindow,
                windowWidth,
                windowHeight,
            ).then(resolve, reject);
        }, 0);
    });
}

export function promptForAccountRemoval(
    data: ProviderAccountDeleteData,
    contentWindow: Window,
    frameHostUrl: string,
    confirmationMessage: string,
): void {
    if (window.confirm(confirmationMessage)) {
        handleDelete(data, contentWindow, frameHostUrl);
    } else {
        contentWindow.postMessage(
            {
                action: 'DeleteProviderAccount',
                messageID: data.messageID,
                accountID: data.accountID,
                success: false,
            },
            frameHostUrl,
        );
    }
}

export async function handleDelete(
    data: ProviderAccountDeleteData,
    contentWindow: Window,
    frameHostUrl: string,
): Promise<void> {
    const baseUrl = window.location.origin + ((window as any).CareEv?.BaseUrl ?? '');
    return deleteProviderAccount(data.accountID).then(
        () => {
            contentWindow.postMessage(
                {
                    action: 'DeleteProviderAccount',
                    success: true,
                    messageID: data.messageID,
                    accountID: data.accountID,
                    baseUrl,
                },
                frameHostUrl,
            );
        },
        (error) => {
            contentWindow.postMessage(
                {
                    action: 'DeleteProviderAccount',
                    messageID: data.messageID,
                    accountID: data.accountID,
                    success: false,
                    message: error?.message ?? 'Delete failed',
                    baseUrl,
                },
                frameHostUrl,
            );
        },
    );
}